import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  OnDestroy,
} from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import {
  PlacesAutocompleteService,
  PlaceSuggestion,
} from '@services/places-autocomplete.service';

@Component({
  selector: 'app-address-form-field',
  templateUrl: './address-form-field.component.html',
  styleUrls: ['./address-form-field.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressFormFieldComponent {
  @ViewChild('addressSearchTextField', { read: ElementRef })
  addressSearchTextField: ElementRef;
  @ViewChild('optionsTemplate', { static: true })
  optionsTemplate: TemplateRef<any>;

  @Input() formGroup: UntypedFormGroup;
  @Input() fieldControlName: string;
  @Input() fieldLabel: string;
  @Input() fieldPlaceholder: string;
  @Input() col: number;
  @Input() validatorOrOpts:
    | ValidatorFn
    | ValidatorFn[]
    | AbstractControlOptions
    | null;
  @Input() addressFieldNameMapping: {
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    lat?: number;
    lon?: number;
    formattedAddress?: string;
  } | null = null;

  @Output() valueChanged = new EventEmitter<any>();

  addressSearchText: any;
  addressResults: PlaceSuggestion[] = [];
  private sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private autocompleteService: PlacesAutocompleteService,
  ) {}

  addressSearchHandleDocumentClick(event: MouseEvent) {
    this.closeAddressSearchResults(false); //don't unfocus the input
  }

  addressSearchHandleDocumentClickRef =
    this.addressSearchHandleDocumentClick.bind(this);

  /* Address Search Support */
  onAddressSearchChange($event: InputEvent) {
    //validate input
    const searchText = ($event.target as HTMLInputElement).value;

    if (!searchText || searchText.length < 3) {
      this.addressResults = []; //clear previous results
      this.closeAddressSearchResults(false); //don't unfocus the input
      return;
    }

    if (!this.sessionToken) {
      this.sessionToken = this.autocompleteService.newSessionToken();
    }

    this.autocompleteService.fetchSuggestions(searchText, { sessionToken: this.sessionToken })
      .then((suggestions) => {
        document.addEventListener(
          'click',
          this.addressSearchHandleDocumentClickRef,
        );

        this.addressResults = suggestions;

        if (this.addressResults.length > 0) {
          this.openOverlay();
        }

        this.changeDetectorRef.markForCheck();
      })
      .catch(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  onAddressSearchResultItemClick(address: any) {
    //close the pop-over
    this.closeAddressSearchResults();
    this.updateFormFromSelectedAddress(address);
    return;
  }

  public updateFormFromSelectedAddress(address: any) {
    if (!address) return;

    //A prediction carries the active autocomplete session, so its lookup is billed as part of that
    //session. A bare place_id - handed over by external callers holding a GeocoderResult - has no
    //session and is a standalone details lookup.
    const fromPrediction = !!address.placePrediction;

    let place: google.maps.places.Place;
    if (fromPrediction) {
      place = address.placePrediction.toPlace();
    } else if (address.place_id) {
      place = new google.maps.places.Place({ id: address.place_id });
    } else {
      return;
    }

    // session token is consumed automatically by the first fetchFields call
    place.fetchFields({ fields: ['addressComponents', 'location', 'formattedAddress'] })
      .then(() => {
        if (fromPrediction) this.sessionToken = null;
        const addr = this.mapToAddressNew(place.addressComponents ?? []);
        this.mapToAddressFields(addr, place.location ?? null, place.formattedAddress ?? null);
        this.valueChanged.emit(addr);
        this.changeDetectorRef.markForCheck();
      })
      .catch(() => {
        //a failed lookup leaves the form untouched, as the legacy status check did
        this.changeDetectorRef.markForCheck();
      });
  }

  private mapToAddressFields(
    address: {},
    location: google.maps.LatLng | null,
    formattedAddress: string | null,
  ) {
    //set the value of the address fields
    this.formGroup.controls[this.fieldControlName].setValue(
      address['address_line_1'],
    );

    if (!this.addressFieldNameMapping) return;

    if (this.addressFieldNameMapping?.city)
      this.formGroup.controls[this.addressFieldNameMapping.city].setValue(
        address['city'],
      );

    if (this.addressFieldNameMapping?.state)
      this.formGroup.controls[this.addressFieldNameMapping.state].setValue(
        address['state'],
      );

    if (this.addressFieldNameMapping?.postal_code)
      this.formGroup.controls[
        this.addressFieldNameMapping.postal_code
      ].setValue(address['postal_code']);

    if (this.addressFieldNameMapping?.country)
      this.formGroup.controls[this.addressFieldNameMapping.country].setValue(
        address['country'],
      );

    if (this.addressFieldNameMapping?.lat && this.addressFieldNameMapping?.lon) {
      const lat = location?.lat();
      const lon = location?.lng();
      this.formGroup.controls[this.addressFieldNameMapping.lat]?.setValue(lat);
      this.formGroup.controls[this.addressFieldNameMapping.lon]?.setValue(lon);
    }

    if (this.addressFieldNameMapping?.formattedAddress)
      this.formGroup.controls[
        this.addressFieldNameMapping.formattedAddress
      ]?.setValue(formattedAddress);

  }

  private mapToAddressNew(addressComponents: google.maps.places.AddressComponent[]) {
    const address = {};
    for (const element of addressComponents) {
      const types = element.types;
      if (types.includes('street_number')) {
        address['address_line_1'] = element.longText + (address['address_line_1'] ?? '');
      } else if (types.includes('route')) {
        address['address_line_1'] = (address['address_line_1'] ?? '') + ' ' + element.longText;
      } else if (types.includes('locality')) {
        address['city'] = element.longText;
      } else if (types.includes('administrative_area_level_1')) {
        address['state'] = element.longText;
      } else if (types.includes('postal_code')) {
        address['postal_code'] = element.longText;
      } else if (types.includes('country')) {
        address['country'] = element.longText;
      }
    }
    return address;
  }

  private closeAddressSearchResults(unfocusInput: boolean = true) {
    //unfocus the input
    if (unfocusInput === true) (document.activeElement as HTMLElement).blur();
    //hide the results
    this.closeOverlay();
  }

  //generic debounce function => move to a utility class
  debounce = (func, timeout = 50) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  };

  overlayRef: OverlayRef;
  openOverlay() {
    if (this.overlayRef) {
      return;
    }
    const overlayConfig: OverlayConfig = {
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.addressSearchTextField)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 0,
            offsetY: 0,
          },
        ]),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    };

    this.overlayRef = this.overlay.create(overlayConfig);
    const panelPortal = new TemplatePortal(
      this.optionsTemplate,
      this.viewContainerRef,
    );
    this.overlayRef.attach(panelPortal);
    this.overlayRef.backdropClick().subscribe(() => {
      this.overlayRef.detach();
    });
  }

  closeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    document.removeEventListener(
      'click',
      this.addressSearchHandleDocumentClickRef,
    );
  }
}
